export interface PaymentInfo {
  confirmationNum: string;
  invoiceNums: string[];
  paymentDate?: string;
  paymentMethod: string;
  amount: string;
}

export enum InvoiceFields {
  ConfirmationNum = "Payment ID",
  InvoiceNums = "Invoice #",
  PaymentDate = "Payment date",
  PaymentMethod = "Payment method",
  Amount = "Amount"
}

export enum InvoiceStatus {
  Paid = "paid",
  Open = "open",
  Overdue = "overdue",
  DueSoon = "due_soon",
  Pending = "pending"
}

export enum PaymentStatus {
  Paid = "Paid",
  Open = "Open",
  Overdue = "Overdue",
  DueSoon = "Due Soon",
  Pending = "Pending"
}

export enum DiscountType {
  PromptPay = "PROMPT_PAY",
  Online = "ONLINE"
}

export type PageSize = 10 | 50 | 100;

export enum SortableColumnKeys {
  InvoiceNo = "invoiceNo",
  PONumber = "poNumber",
  ShipTo = "shipTo",
  Total = "total",
  DueDate = "dueDate",
  Status = "status"
}

export interface SortableColumn {
  property: SortableColumnKeys;
  direction: SortOrder;
}

export enum SortOrder {
  ASC = "asc",
  DESC = "desc"
}

export const InvoicesAndPaymentsTabs = {
  invoices: {
    id: "invoices",
    label: "Invoices"
  },
  credits: {
    id: "credits",
    label: "Credits"
  },
  statements: {
    id: "statements",
    label: "Statements"
  }
};

export interface InvoicesViewProps {
  isLoadingInvoices: boolean;
  invoiceError: string | null;
  invoiceList: InvoiceList;
  pageSize: PageSize;
  currentPage: number;
  handlePageChange: (page: number) => void;
  handlePageSizeChange: (pageSize: PageSize) => void;
  handleSort: (sortInfo: SortableColumn) => void;
  sortDirection?: SortOrder;
  sortProperty?: SortableColumnKeys;
}

export interface InvoiceListProps {
  invoiceList: Invoice[];
  pageSize: PageSize;
  totalPage: number;
  totalInvoices: number;
  currentPage: number;
  sortDirection?: SortOrder;
  sortProperty?: SortableColumnKeys;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: PageSize) => void;
  onSort: (sortInfo: SortableColumn) => void;
}

export const payableStatuses = [
  PaymentStatus.Open,
  PaymentStatus.Overdue,
  PaymentStatus.DueSoon
];

export interface InvoicePriceSummary {
  totalPrice?: number;
  totalDoses?: number;
  amountDue: number;
  onlineDiscount?: number;
  subtotal: number;
  tax?: number;
  netInvoice: number;
  promptPayDiscount: number;
  promptPayDiscountedTotal: number;
  promptPayDueDate?: string;
}

export interface InvoiceBalanceSummary {
  total: string;
  credits: string;
  currency: string;
  shipToAddresses: Array<InvoiceAddress>;
}

export interface Invoice {
  invoiceNo: string;
  subtotal: number;
  total: number;
  taxes: number;
  currency: string;
  poNumber?: string;
  status: InvoiceStatus;
  invoiceDate: string;
  dueDate: string;
  shipTo: InvoiceAddress;
  billTo: InvoiceAddress;
  soldTo: InvoiceAddress;
  discounts: InvoiceDiscount[];
  orderDate?: string; // not returned from BE yet
  orderNumber?: string;
  orderLineItems: InvoiceItem[];
  paymentTermsDescription: string;
  notes: string[];
  payment?: InvoicePayment; // not returned from BE yet
}

export interface InvoiceDiscount {
  discountType: DiscountType;
  discountAmount: number;
  discountExpiration: string;
}

export interface InvoicePayment {
  confirmationNumber?: string;
  paymentDate?: string;
  paymentMethod?: string;
  paymentAmount?: string;
  paymentScheduled?: boolean;
  paymentScheduledDate?: string;
}

export interface InvoiceAddress {
  id?: number;
  name?: string;
  line1: string;
  line2?: string;
  city: string;
  state: string;
  postalCode: string;
}

export interface InvoiceItem {
  itemDescription: string;
  contractPrice: number;
  quantity: number;
  subtotal: number;
  ndcNumber: string;
}

export interface InvoiceList {
  invoices: Invoice[];
  pagination: {
    pageNumber: number;
    pageSize: number;
    total: number;
  };
  filters: {
    status: null;
    shippingLocation: null;
  };
  sorting: {
    sortBy: SortableColumnKeys;
    sortDirection: SortOrder;
  };
}
